import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export function useCustomLayers(viewId, options = {}) {
  const [layers, setLayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth(); // Assuming auth context is available

  const fetchLayers = useCallback(async () => {
    if (!viewId) {
      setLayers([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (options.isVisible !== undefined) {
        queryParams.append('isVisible', options.isVisible);
      }

      const response = await fetch(`/api/custom-layers/view/${viewId}?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch custom layers');
      }

      const data = await response.json();
      setLayers(data);
    } catch (err) {
      console.error('Error fetching custom layers:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [viewId, options.isVisible]);

  useEffect(() => {
    fetchLayers();
  }, [fetchLayers]);

  return {
    layers,
    isLoading,
    error,
    refetch: fetchLayers
  };
}

export function useCustomLayerGeoJSON(layerId, options = {}) {
  const [geoJSON, setGeoJSON] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const resolution = options.resolution;

  useEffect(() => {
    if (!layerId) return;

    const fetchGeoJSON = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        if (resolution !== undefined) {
          queryParams.append('resolution', resolution);
        }
        
        const response = await fetch(`/api/custom-layers/${layerId}/geojson?${queryParams.toString()}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch layer GeoJSON');
        }

        const data = await response.json();
        setGeoJSON(data);
      } catch (err) {
        console.error(`Error fetching GeoJSON for layer ${layerId}:`, err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeoJSON();
  }, [layerId, resolution]);

  return { geoJSON, isLoading, error };
}

export function useCustomLayerTopoJSON(layerId, options = {}) {
  const [topoJSON, setTopoJSON] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const resolution = options.resolution;

  useEffect(() => {
    if (!layerId) return;

    const fetchTopoJSON = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        if (resolution !== undefined) {
          queryParams.append('resolution', resolution);
        }
        
        const response = await fetch(`/api/custom-layers/${layerId}/topojson?${queryParams.toString()}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch layer TopoJSON');
        }

        const data = await response.json();
        setTopoJSON(data);
      } catch (err) {
        console.error(`Error fetching TopoJSON for layer ${layerId}:`, err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopoJSON();
  }, [layerId, resolution]);

  return { topoJSON, isLoading, error };
}
